import React, { useEffect, useMemo, useRef, useState } from "react";
import { format } from "date-fns";
import {
  Loader2,
  RefreshCw,
  Search,
  Upload,
  Download,
  Trash2,
  FileText,
  FileImage,
  File as FileIcon,
  Edit3,
  Paperclip,
} from "lucide-react";
import { toast } from "sonner";

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import {
  DOCUMENT_CATEGORIES,
  listDocuments,
  getDocumentStats,
  uploadDocument,
  updateDocument,
  deleteDocument,
  documentDownloadUrl,
  clearAdminToken,
} from "@/lib/api";

const ACCEPT = ".pdf,.jpg,.jpeg,.png,.docx,.doc";
const ACCEPTED_EXTS = ["pdf", "jpg", "jpeg", "png", "docx", "doc"];

const formatBytes = (n) => {
  if (!n) return "0 B";
  const u = ["B", "KB", "MB", "GB"];
  let i = 0;
  let v = n;
  while (v >= 1024 && i < u.length - 1) {
    v /= 1024;
    i += 1;
  }
  return `${v.toFixed(v >= 10 || i === 0 ? 0 : 1)} ${u[i]}`;
};

const extOf = (filename) =>
  (filename?.split(".").pop() || "").toLowerCase().trim();

const IconForFile = ({ filename, contentType, className }) => {
  const e = extOf(filename);
  if (e === "pdf") return <FileText className={className} style={{ color: "#B91C1C" }} />;
  if (["jpg", "jpeg", "png"].includes(e) || (contentType || "").startsWith("image/"))
    return <FileImage className={className} style={{ color: "#0F766E" }} />;
  if (["docx", "doc"].includes(e)) return <FileText className={className} style={{ color: "#1D4ED8" }} />;
  return <FileIcon className={className} style={{ color: "#475569" }} />;
};

const labelForCategory = (v) =>
  DOCUMENT_CATEGORIES.find((c) => c.value === v)?.label || v;

export default function DocumentsPanel({ onSessionExpired }) {
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [filterCat, setFilterCat] = useState("all");
  const [showUpload, setShowUpload] = useState(false);
  const [active, setActive] = useState(null);

  const refresh = async () => {
    try {
      setLoading(true);
      const [docs, s] = await Promise.all([listDocuments(), getDocumentStats()]);
      setItems(docs);
      setStats(s);
    } catch (e) {
      if (e?.response?.status === 401) {
        clearAdminToken();
        onSessionExpired?.();
        toast.error("Your session expired. Please sign in again.");
        return;
      }
      toast.error("Failed to load documents.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return items
      .filter((d) => filterCat === "all" || d.category === filterCat)
      .filter((d) =>
        !q ||
        [d.display_name, d.filename, d.notes, d.attachment_label]
          .filter(Boolean)
          .some((v) => String(v).toLowerCase().includes(q)),
      );
  }, [items, filterCat, query]);

  const onUploaded = (record) => {
    setItems((prev) => [record, ...prev]);
    setShowUpload(false);
    // Refresh stats in background
    getDocumentStats().then(setStats).catch(() => {});
    toast.success("Document uploaded.");
  };

  const onPatched = (record) => {
    setItems((prev) => prev.map((d) => (d.id === record.id ? record : d)));
    if (active?.id === record.id) setActive(record);
    getDocumentStats().then(setStats).catch(() => {});
  };

  const onDelete = async (id) => {
    if (!window.confirm("Permanently delete this document? This cannot be undone.")) return;
    try {
      await deleteDocument(id);
      setItems((prev) => prev.filter((d) => d.id !== id));
      if (active?.id === id) setActive(null);
      getDocumentStats().then(setStats).catch(() => {});
      toast.success("Document deleted.");
    } catch {
      toast.error("Failed to delete document.");
    }
  };

  const filterChips = [
    { id: "all", label: "All Documents", count: stats?.total || 0 },
    ...DOCUMENT_CATEGORIES.map((c) => ({
      id: c.value,
      label: c.label,
      count: stats?.by_category?.[c.value]?.count || 0,
    })),
  ];

  return (
    <div data-testid="documents-panel">
      <div className="flex items-end justify-between gap-6 flex-wrap">
        <div>
          <div className="text-[11px] uppercase tracking-[0.22em] font-medium" style={{ color: "#D4AF37" }}>
            Document Vault
          </div>
          <h1 className="font-display mt-2 text-3xl sm:text-4xl font-bold tracking-tight" style={{ color: "#0B2545" }}>
            Internal documents
          </h1>
          <p className="mt-1 text-sm" style={{ color: "#475569" }}>
            Secure storage for business, staff, facility, client, and financial records.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={refresh}
            className="h-10"
            style={{ borderColor: "#E2E8F0", color: "#0B2545" }}
            data-testid="documents-refresh-button"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
          <Button
            onClick={() => setShowUpload(true)}
            className="h-10"
            style={{ background: "#0B2545", color: "#FFFFFF" }}
            data-testid="documents-upload-open"
          >
            <Upload className="h-4 w-4 mr-2" />
            Upload Document
          </Button>
        </div>
      </div>

      <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
        {DOCUMENT_CATEGORIES.map((c) => {
          const s = stats?.by_category?.[c.value] || { count: 0, bytes: 0 };
          return (
            <button
              key={c.value}
              type="button"
              onClick={() => setFilterCat(c.value)}
              className="text-left rounded-xl border bg-white px-5 py-4 transition-all hover:-translate-y-[1px]"
              style={{ borderColor: filterCat === c.value ? "#0B2545" : "#E2E8F0" }}
              data-testid={`doc-cat-card-${c.value}`}
            >
              <div className="text-[11px] uppercase tracking-[0.22em] font-medium" style={{ color: "#475569" }}>
                {c.label}
              </div>
              <div className="mt-2 flex items-end gap-3">
                <div className="font-display text-3xl font-bold" style={{ color: "#0B2545" }}>{s.count}</div>
                <div className="text-[11px] mb-1" style={{ color: "#7A5E12" }}>{formatBytes(s.bytes)}</div>
              </div>
            </button>
          );
        })}
      </div>

      <div className="mt-6 flex flex-wrap gap-2" data-testid="document-filters">
        {filterChips.map((f) => {
          const isActive = filterCat === f.id;
          return (
            <button
              key={f.id}
              type="button"
              onClick={() => setFilterCat(f.id)}
              className="inline-flex items-center gap-2 h-9 px-3.5 rounded-full border text-xs font-semibold transition-all"
              style={{
                borderColor: isActive ? "#0B2545" : "#E2E8F0",
                background: isActive ? "#0B2545" : "#FFFFFF",
                color: isActive ? "#FFFFFF" : "#0B2545",
              }}
              data-testid={`doc-filter-${f.id}`}
            >
              {f.label}
              <span
                className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full text-[10px] font-bold"
                style={{
                  background: isActive ? "rgba(255,255,255,0.18)" : "#F1F5F9",
                  color: isActive ? "#FFFFFF" : "#475569",
                }}
              >
                {f.count}
              </span>
            </button>
          );
        })}
      </div>

      <div className="mt-4 flex items-center gap-3">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4" style={{ color: "#94A3B8" }} />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search documents, notes, attached records..."
            className="h-10 pl-9"
            data-testid="document-search-input"
          />
        </div>
      </div>

      <div className="mt-5 overflow-hidden rounded-xl border bg-white" style={{ borderColor: "#E2E8F0" }}>
        {loading ? (
          <div className="flex items-center justify-center gap-3 py-16" style={{ color: "#475569" }}>
            <Loader2 className="h-5 w-5 animate-spin" />
            <span className="text-sm">Loading documents...</span>
          </div>
        ) : filtered.length === 0 ? (
          <div className="py-20 text-center">
            <p className="text-sm" style={{ color: "#475569" }}>
              {query || filterCat !== "all" ? "No documents match your filters." : "No documents uploaded yet."}
            </p>
            {!query && filterCat === "all" ? (
              <Button
                onClick={() => setShowUpload(true)}
                variant="outline"
                className="mt-4 h-9"
                style={{ borderColor: "#E2E8F0", color: "#0B2545" }}
                data-testid="documents-empty-upload"
              >
                <Upload className="h-3.5 w-3.5 mr-2" />
                Upload your first document
              </Button>
            ) : null}
          </div>
        ) : (
          <Table data-testid="documents-table">
            <TableHeader>
              <TableRow style={{ background: "#F8FAFC" }}>
                <TableHead className="text-[11px] uppercase tracking-[0.18em]" style={{ color: "#475569" }}>Document</TableHead>
                <TableHead className="text-[11px] uppercase tracking-[0.18em]" style={{ color: "#475569" }}>Category</TableHead>
                <TableHead className="text-[11px] uppercase tracking-[0.18em]" style={{ color: "#475569" }}>Attached To</TableHead>
                <TableHead className="text-[11px] uppercase tracking-[0.18em]" style={{ color: "#475569" }}>Size</TableHead>
                <TableHead className="text-[11px] uppercase tracking-[0.18em]" style={{ color: "#475569" }}>Uploaded</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((d) => (
                <TableRow key={d.id} className="hover:bg-[#F8FAFC]" data-testid={`document-row-${d.id}`}>
                  <TableCell>
                    <div className="flex items-center gap-3 min-w-0">
                      <IconForFile filename={d.filename} contentType={d.content_type} className="h-5 w-5 shrink-0" />
                      <div className="min-w-0">
                        <div className="font-semibold text-sm truncate" style={{ color: "#0B2545" }}>{d.display_name}</div>
                        <div className="text-[11px] truncate" style={{ color: "#475569" }}>{d.filename}</div>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <span
                      className="inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-semibold"
                      style={{ background: "#F8FAFC", color: "#0B2545", borderColor: "#E2E8F0" }}
                    >
                      {labelForCategory(d.category)}
                    </span>
                  </TableCell>
                  <TableCell className="text-xs" style={{ color: "#475569" }}>
                    {d.attachment_label ? (
                      <span className="inline-flex items-center gap-1.5">
                        <Paperclip className="h-3 w-3" style={{ color: "#D4AF37" }} />
                        {d.attachment_label}
                      </span>
                    ) : "—"}
                  </TableCell>
                  <TableCell className="text-xs" style={{ color: "#475569" }}>{formatBytes(d.size_bytes)}</TableCell>
                  <TableCell className="text-xs" style={{ color: "#475569" }}>
                    {d.uploaded_at ? format(new Date(d.uploaded_at), "PP") : "—"}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      <a
                        href={documentDownloadUrl(d.id)}
                        className="inline-flex items-center gap-1.5 h-8 px-2.5 rounded-md text-xs font-medium border transition-all hover:bg-[#F8FAFC]"
                        style={{ borderColor: "#E2E8F0", color: "#0B2545" }}
                        data-testid={`doc-download-${d.id}`}
                      >
                        <Download className="h-3.5 w-3.5" />
                        Download
                      </a>
                      <Button size="sm" variant="ghost" className="text-xs h-8 px-2" style={{ color: "#0B2545" }} onClick={() => setActive(d)} data-testid={`doc-manage-${d.id}`}>
                        <Edit3 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      <UploadDialog open={showUpload} onClose={() => setShowUpload(false)} onUploaded={onUploaded} />
      <DocumentDetailDialog
        record={active}
        onClose={() => setActive(null)}
        onUpdate={onPatched}
        onDelete={onDelete}
      />
    </div>
  );
}

// ---------- Upload Dialog ----------
const UploadDialog = ({ open, onClose, onUploaded }) => {
  const inputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [category, setCategory] = useState("business");
  const [displayName, setDisplayName] = useState("");
  const [notes, setNotes] = useState("");
  const [attachType, setAttachType] = useState("none");
  const [attachId, setAttachId] = useState("");
  const [attachLabel, setAttachLabel] = useState("");
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    if (open) {
      setFile(null);
      setCategory("business");
      setDisplayName("");
      setNotes("");
      setAttachType("none");
      setAttachId("");
      setAttachLabel("");
      setBusy(false);
      setProgress(0);
    }
  }, [open]);

  const pickFile = (f) => {
    if (!f) return;
    const ext = (f.name.split(".").pop() || "").toLowerCase();
    if (!ACCEPTED_EXTS.includes(ext)) {
      toast.error("Only PDF, JPG, PNG, DOCX, or DOC files are allowed.");
      return;
    }
    if (f.size > 25 * 1024 * 1024) {
      toast.error("File exceeds 25 MB limit.");
      return;
    }
    setFile(f);
    if (!displayName) setDisplayName(f.name.replace(/\.[^.]+$/, ""));
  };

  const onDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const f = e.dataTransfer?.files?.[0];
    if (f) pickFile(f);
  };

  const submit = async () => {
    if (!file) {
      toast.error("Please choose a file.");
      return;
    }
    const fd = new FormData();
    fd.append("file", file);
    fd.append("category", category);
    if (displayName.trim()) fd.append("display_name", displayName.trim());
    if (notes.trim()) fd.append("notes", notes.trim());
    if (attachType !== "none" && attachId.trim()) {
      fd.append("attachment_type", attachType);
      fd.append("attachment_id", attachId.trim());
      if (attachLabel.trim()) fd.append("attachment_label", attachLabel.trim());
    }
    try {
      setBusy(true);
      setProgress(0);
      const rec = await uploadDocument(fd, (evt) => {
        if (evt.total) setProgress(Math.round((evt.loaded / evt.total) * 100));
      });
      onUploaded(rec);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Upload failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && !busy && onClose()}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="document-upload-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl" style={{ color: "#0B2545" }}>Upload document</DialogTitle>
          <DialogDescription>PDF, JPG, PNG, DOCX, or DOC · Max 25 MB.</DialogDescription>
        </DialogHeader>

        <div
          onDragOver={(e) => { e.preventDefault(); }}
          onDrop={onDrop}
          onClick={() => inputRef.current?.click()}
          className="mt-2 rounded-xl border-2 border-dashed px-6 py-10 text-center cursor-pointer transition-colors hover:bg-[#F8FAFC]"
          style={{ borderColor: file ? "#0B2545" : "#CBD5E1" }}
          data-testid="document-dropzone"
        >
          <input
            ref={inputRef}
            type="file"
            accept={ACCEPT}
            className="hidden"
            onChange={(e) => pickFile(e.target.files?.[0])}
            data-testid="document-file-input"
          />
          <Upload className="h-7 w-7 mx-auto" style={{ color: "#D4AF37" }} />
          {file ? (
            <div className="mt-3">
              <div className="font-semibold text-sm" style={{ color: "#0B2545" }}>{file.name}</div>
              <div className="text-xs mt-0.5" style={{ color: "#475569" }}>{formatBytes(file.size)} · click to replace</div>
            </div>
          ) : (
            <>
              <div className="mt-3 font-semibold text-sm" style={{ color: "#0B2545" }}>Click to choose or drag & drop</div>
              <div className="text-xs mt-1" style={{ color: "#475569" }}>PDF · JPG · PNG · DOCX · DOC</div>
            </>
          )}
        </div>

        <div className="grid gap-3 sm:grid-cols-2 mt-4">
          <Field label="Category">
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger className="h-10" data-testid="document-category-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {DOCUMENT_CATEGORIES.map((c) => (
                  <SelectItem key={c.value} value={c.value} data-testid={`document-category-option-${c.value}`}>{c.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Display name">
            <Input value={displayName} onChange={(e) => setDisplayName(e.target.value)} placeholder="e.g. Acme Health MSA 2026" className="h-10" data-testid="document-name-input" />
          </Field>
          <div className="sm:col-span-2">
            <Field label="Notes (optional)">
              <Textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Internal notes — expiration date, signing party, references..." data-testid="document-notes-input" />
            </Field>
          </div>
          <Field label="Attach to (optional)">
            <Select value={attachType} onValueChange={setAttachType}>
              <SelectTrigger className="h-10" data-testid="document-attach-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">None</SelectItem>
                <SelectItem value="lead">Consumer lead</SelectItem>
                <SelectItem value="facility">Facility / provider</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          {attachType !== "none" ? (
            <>
              <Field label="Record ID">
                <Input value={attachId} onChange={(e) => setAttachId(e.target.value)} placeholder="Paste the record ID" className="h-10" data-testid="document-attach-id-input" />
              </Field>
              <div className="sm:col-span-2">
                <Field label="Record label (e.g. facility name)">
                  <Input value={attachLabel} onChange={(e) => setAttachLabel(e.target.value)} placeholder="Acme Medical Group" className="h-10" data-testid="document-attach-label-input" />
                </Field>
              </div>
            </>
          ) : null}
        </div>

        {busy ? (
          <div className="mt-4">
            <div className="text-[11px] uppercase tracking-[0.18em] font-medium mb-1" style={{ color: "#475569" }}>Uploading… {progress}%</div>
            <div className="h-2 rounded-full overflow-hidden" style={{ background: "#F1F5F9" }}>
              <div className="h-full transition-all" style={{ width: `${progress}%`, background: "#D4AF37" }} />
            </div>
          </div>
        ) : null}

        <div className="mt-5 flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} disabled={busy} className="h-10" data-testid="document-upload-cancel">Cancel</Button>
          <Button onClick={submit} disabled={busy || !file} className="h-10" style={{ background: "#0B2545", color: "#FFFFFF" }} data-testid="document-upload-submit">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Upload"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// ---------- Detail / Edit Dialog ----------
const DocumentDetailDialog = ({ record, onClose, onUpdate, onDelete }) => {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (record) {
      setForm({
        display_name: record.display_name || "",
        category: record.category,
        notes: record.notes || "",
        attachment_type: record.attachment_type || "none",
        attachment_id: record.attachment_id || "",
        attachment_label: record.attachment_label || "",
      });
    }
  }, [record?.id]);

  if (!record || !form) return null;

  const save = async () => {
    try {
      setSaving(true);
      const body = {
        display_name: form.display_name.trim() || undefined,
        category: form.category,
        notes: form.notes.trim() || null,
        attachment_type: form.attachment_type === "none" ? null : form.attachment_type,
        attachment_id: form.attachment_type === "none" ? null : (form.attachment_id.trim() || null),
        attachment_label: form.attachment_type === "none" ? null : (form.attachment_label.trim() || null),
      };
      const updated = await updateDocument(record.id, body);
      onUpdate(updated);
      toast.success("Document updated.");
      onClose();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Failed to update.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!record} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="document-detail-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-2" style={{ color: "#0B2545" }}>
            <IconForFile filename={record.filename} contentType={record.content_type} className="h-5 w-5" />
            <span className="truncate">{record.display_name}</span>
          </DialogTitle>
          <DialogDescription>
            {record.filename} · {formatBytes(record.size_bytes)} · Uploaded {record.uploaded_at ? format(new Date(record.uploaded_at), "PPpp") : "—"}
          </DialogDescription>
        </DialogHeader>

        <div className="flex gap-2 mt-2">
          <a
            href={documentDownloadUrl(record.id)}
            className="inline-flex items-center gap-2 h-10 px-4 rounded-md font-semibold text-sm"
            style={{ background: "#0B2545", color: "#FFFFFF" }}
            data-testid="document-detail-download"
          >
            <Download className="h-4 w-4" />
            Download
          </a>
          <Button
            variant="ghost"
            className="h-10"
            style={{ color: "#B91C1C" }}
            onClick={() => onDelete(record.id)}
            data-testid="document-detail-delete"
          >
            <Trash2 className="h-4 w-4 mr-1.5" />
            Delete
          </Button>
        </div>

        <div className="grid gap-3 sm:grid-cols-2 mt-4">
          <Field label="Display name">
            <Input value={form.display_name} onChange={(e) => setForm((s) => ({ ...s, display_name: e.target.value }))} className="h-10" data-testid="document-edit-name" />
          </Field>
          <Field label="Category">
            <Select value={form.category} onValueChange={(v) => setForm((s) => ({ ...s, category: v }))}>
              <SelectTrigger className="h-10" data-testid="document-edit-category"><SelectValue /></SelectTrigger>
              <SelectContent>
                {DOCUMENT_CATEGORIES.map((c) => (
                  <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <div className="sm:col-span-2">
            <Field label="Internal notes">
              <Textarea rows={4} value={form.notes} onChange={(e) => setForm((s) => ({ ...s, notes: e.target.value }))} placeholder="Expiration, signing party, references..." data-testid="document-edit-notes" />
            </Field>
          </div>
          <Field label="Attached to">
            <Select value={form.attachment_type} onValueChange={(v) => setForm((s) => ({ ...s, attachment_type: v }))}>
              <SelectTrigger className="h-10" data-testid="document-edit-attach-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">None</SelectItem>
                <SelectItem value="lead">Consumer lead</SelectItem>
                <SelectItem value="facility">Facility / provider</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          {form.attachment_type !== "none" ? (
            <>
              <Field label="Record ID">
                <Input value={form.attachment_id} onChange={(e) => setForm((s) => ({ ...s, attachment_id: e.target.value }))} className="h-10" data-testid="document-edit-attach-id" />
              </Field>
              <div className="sm:col-span-2">
                <Field label="Record label">
                  <Input value={form.attachment_label} onChange={(e) => setForm((s) => ({ ...s, attachment_label: e.target.value }))} className="h-10" data-testid="document-edit-attach-label" />
                </Field>
              </div>
            </>
          ) : null}
        </div>

        <div className="mt-5 flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} className="h-10" data-testid="document-detail-close">Cancel</Button>
          <Button onClick={save} disabled={saving} className="h-10" style={{ background: "#0B2545", color: "#FFFFFF" }} data-testid="document-detail-save">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save changes"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const Field = ({ label, children }) => (
  <div>
    <div className="text-[11px] uppercase tracking-[0.18em] font-medium mb-1" style={{ color: "#0B2545" }}>{label}</div>
    {children}
  </div>
);
